import { Box, Grid, Typography } from "@mui/material";
import * as React from "react";
import useBackend, { useUserPrivilegeKeyPermissionsMap } from "hooks/use_backend";
import { REACT_QUERY_KEYS } from "enums/react_query_key_enum";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import HelpedAdornedTextField from "components/general/helped_adorned_text_field";
import { EditableCard } from "components/general/editable_card";
import Snackbar from "scripts/snackbar";
import {commandUpdateSiteConfig} from "scripts/api_v2/commands_client";
import {useParams} from "react-router-dom";
import {useSiteData} from "hooks/use_site";
import {CheckBox} from "components/general/checkbox";
import BatteryCapacityVisualization from "components/installation/battery_capacity_visualization";
import {findPeakShavingEligibleBatteryNode, getPeakShavingStatus, hasValue, isValidPeakShavingMinSoc, resolveSocLimits} from "components/installation/peak_shaving_utils";
import { useQuery } from "@tanstack/react-query";
import { fetchSiteProperties } from "../../scripts/api_v2/properties_client";
import useSchematic from "hooks/use_schematic";
import { useAmplitudeTrack } from "scripts/amplitude_utils";

export default function PeakShavingCard({ required_privilege, ...props }) {
  const params = useParams();
  const backend = useBackend();
  const permissionMap = useUserPrivilegeKeyPermissionsMap();
  const { trackButtonClicked } = useAmplitudeTrack();

  const [editingConfig, setEditingConfig] = React.useState(false);
  const [configEdits, setConfigEdits] = React.useState({});
  const [confCopy, setConfCopy] = React.useState({});

  let { data: siteData, error, isLoading, refetch } = useSiteData();
  const { nodeMetering } = useSchematic();

  const queryClient = useQueryClient();
  
  const {
    data: siteProps,
    error: sitePropsError,
    isLoading: sitePropsLoading,
    refetch: sitePropsRefetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_PROPERTIES, backend.fetch, params.site_id],
    queryFn: () => fetchSiteProperties(backend.fetch, params.site_id),
  });

  const {
      enabled: peakShavingEnabled,
      hasEligibleBattery: hasPeakShavingEligibleBattery,
      effectivelyActive: peakShavingEffectivelyActive,
  } = getPeakShavingStatus(siteData);
    const peakShavingCanToggle = editingConfig && (hasPeakShavingEligibleBattery || peakShavingEnabled);
    const {minSocLimit, maxSocLimit} = resolveSocLimits(siteProps?.hard_minimum_soc, siteProps?.hard_maximum_soc);
    const peakShavingSocRangeText = `${minSocLimit}-${maxSocLimit}`;
    const peakShavingMinSoc = siteData?.config?.peak_shaving_min_soc;
    const peakShavingMinSocHasValue = hasValue(peakShavingMinSoc);
    const peakShavingMinSocIsValid = isValidPeakShavingMinSoc(peakShavingMinSoc, minSocLimit, maxSocLimit);
    const peakShavingMinSocRequiredMissing = editingConfig && peakShavingEnabled && !peakShavingMinSocHasValue;
    const peakShavingMinSocInvalid = editingConfig && peakShavingEnabled && peakShavingMinSocHasValue && !peakShavingMinSocIsValid;

    const peakShavingMinSocDisplayValue = editingConfig
        ? (peakShavingMinSoc ?? "")
        : (peakShavingEnabled ? (peakShavingMinSoc ?? "") : "Not applicable");
    const peakShavingMinSocForVisualization = peakShavingEnabled ? peakShavingMinSoc : null;
    const showBatteryCapacityVisualization = hasPeakShavingEligibleBattery;
    const peakShavingInactiveWithoutEligibleBattery = peakShavingEnabled && !hasPeakShavingEligibleBattery;
    const eligibleBatteryNode = findPeakShavingEligibleBatteryNode(siteData);
    const currentSoc = eligibleBatteryNode ? nodeMetering(eligibleBatteryNode.id, "soc").value : null;

    const {mutate: configMutation, isPending: isMutating} = useMutation({
        mutationFn: async ({commit, configEdits, mutatedField, mutatedValue, isString}
        ) => {
            if (!commit) {
                // If commit is false, we don't perform the mutation
                return await Promise.resolve(); // Return a resolved promise to avoid rejection
            } else { // Do the actual commit
                return await commandUpdateSiteConfig(backend.fetch, params.site_id, configEdits);
            }
        },
        onMutate: async ({commit, configEdits, mutatedField, mutatedValue, isString}) => {
            //If we always commit, consider using this to snapshot the previous value
            //const previousSiteData = queryClient.getQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, siteId])
            const oldSiteData = {...siteData, "config": confCopy}

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id] });

      // Optimistically update to the new value(s)
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const updated = { ...prev.config };
        if (commit) {
          Object.values(configEdits).forEach((edit) => {
            updated[edit.field] = edit.value;
          });
        } else {
          updated[mutatedField] = isString ? mutatedValue.toString() : mutatedValue;
        }
        return { ...prev, config: updated };
      });

      return { oldSiteData };
    },
    onError: (err, variables, context) => {
      //Using handleCancel because its reused, but we should prefer to use the context.oldSiteData to rollback...
      handleCancelConfig();
      Snackbar.error("Could not update config: " + err);
    },
    // Always refetch after commit, error or success:
    onSettled: async (data, error, variables) => {
      const { commit, configEdits } = variables;
      if (commit) {
        await refetch();
        if (!error) {
          // The refetch above can race with the backend applying the change and briefly
          // return stale data; keep showing the values we just saved until a later,
          // genuinely up-to-date refresh confirms them.
          queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
            if (!prev) return prev;
            const updated = { ...prev.config };
            Object.values(configEdits).forEach((edit) => {
              updated[edit.field] = edit.value;
            });
            return { ...prev, config: updated };
          });
        }
        setConfigEdits({});
        setEditingConfig(false);
      }
    },
  });

  function handleEditConfig() {
    trackButtonClicked('peak_shaving_card_edit', 'peak_shaving_card', { site_id: params.site_id });
    setConfCopy(siteData ? siteData.config : {});
    setEditingConfig(true);
  }

  function handleCancelConfig() {
    queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
      return { ...prev, config: confCopy };
    });
    setConfigEdits({});
    setEditingConfig(false);
  }

  function handleConfigChange(field, value, isString = false) {
      const processedValue = field === "peak_shaving_min_soc"
          ? value
          : (isString ? value.toString() : (typeof (value) == "boolean" ||
          typeof (value) == "number" ? value : (value.length > 0 ? parseInt(value) : 0)))
      const updated = {...configEdits};
      updated[field] = {field: field, value: processedValue, prev_value: confCopy[field]};
      setConfigEdits(updated)

      const mutationData = {
          commit: false,
          configEdits: updated, mutatedField: field, mutatedValue: processedValue, isString: isString
      };

      configMutation(mutationData);
  }

  async function commitConfigChange() {
      const peakShavingWarningText = "You are setting a value for Peak shaving minimum soc but peak shaving is not enabled. Are you aware this value will not be taken into account?";

      if (peakShavingEnabled && !hasPeakShavingEligibleBattery) {
          Snackbar.error("Peak shaving cannot be active without a linked battery with control enabled and capacity > 0.");
          return;
      }

      if (peakShavingEnabled && !peakShavingMinSocHasValue) {
          Snackbar.error("Peak shaving min soc is required when peak shaving is enabled.");
          return;
      }

      if (peakShavingEnabled && !peakShavingMinSocIsValid) {
          Snackbar.error(`Peak shaving min soc must be an integer between ${minSocLimit} and ${maxSocLimit}.`);
          return;
      }

      if (!peakShavingEnabled && peakShavingMinSocHasValue) {
          const confirmed = window.confirm(peakShavingWarningText);
          if (!confirmed) {
              return;
          }
      }

      let processedConfigEdits = {...configEdits};
      if (processedConfigEdits["peak_shaving_min_soc"] && peakShavingMinSocHasValue) {
          const parsedMinSoc = Number(peakShavingMinSoc);
          if (Number.isFinite(parsedMinSoc)) {
              processedConfigEdits["peak_shaving_min_soc"] = {
                  ...processedConfigEdits["peak_shaving_min_soc"],
                  value: parsedMinSoc
              };
          }
      }

      if (Object.values(configEdits).length === 0) {
          handleCancelConfig()
      } else {
          const mutationData = {
              commit: true,
              configEdits: processedConfigEdits, mutatedField: null, mutatedValue: null, isString: false
          };
          configMutation(mutationData);
      }
  }

    return <EditableCard editable title="Peak Shaving" width={"100%"} elevation={0}
                         editing={editingConfig} handleEdit={handleEditConfig} handleSave={commitConfigChange}
                         handleCancel={handleCancelConfig} permissionMap={permissionMap}
                         required_privilege={required_privilege} {...props}>
        {siteData && siteData.config ?
            <Grid container sx={{mt: 1}}>
                <Grid md={6} xs={12} item width={"80%"}
                      sx={{display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center"}} px={2} py={2}>
                    <CheckBox label="PEAK SHAVING - OFFTAKE"
                              info="When enabled, max grid offtake is enforced as a hard limit — the battery will discharge to ensure grid power never exceeds this value."
                              checked={peakShavingEnabled}
                              setChecked={(value) => handleConfigChange("peak_shaving_offtake", value, false)}
                              disabled={!peakShavingCanToggle}
                              size="medium"/>
                    {peakShavingEffectivelyActive &&
                        <Box sx={{pt: 0.75, display: "flex", flexDirection: "column", gap: 0.1}}>
                            <Typography variant="caption" color="error.main" sx={{fontWeight: 700, lineHeight: 1.3}}>
                                Peak shaving status: ACTIVE
                            </Typography>
                            <Typography variant="caption" color="error.main" sx={{fontWeight: 500, lineHeight: 1.3}}>
                                Max grid offtake is enforced as a hard limit — battery actively prevents exceeding it.
                            </Typography>
                        </Box>}
                    {!hasPeakShavingEligibleBattery && !peakShavingEnabled &&
                            <Box sx={{pt: 0.75, display: "flex", flexDirection: "column", gap: 0.1}}>
                            <Typography variant="caption" color="warning.main" sx={{fontWeight: 700, lineHeight: 1.3}}>
                                Can not enable peak shaving
                            </Typography>
                            <Typography variant="caption" color="warning.main" sx={{fontWeight: 500, lineHeight: 1.3}}>
                                Add and link a battery, set capacity and enable control in order to enable peak shaving.
                            </Typography>
                        </Box>}
                    {peakShavingInactiveWithoutEligibleBattery &&
                             <Box sx={{pt: 0.75, display: "flex", flexDirection: "column", gap: 0.1}}>
                            <Typography variant="caption" color="warning.main" sx={{fontWeight: 700, lineHeight: 1.3}}>
                                Peak shaving status: INACTIVE
                            </Typography>
                            <Typography variant="caption" color="warning.main" sx={{fontWeight: 500, lineHeight: 1.3}}>
                                No controllable battery available for peak shaving, check capacity and control settings.
                            </Typography>
                        </Box>}
                </Grid>
                <Grid md={6} xs={12} item width={"80%"}
                      sx={{display: "flex", alignItems: "center", justifyContent: "center"}} px={2} py={2}>
                    <HelpedAdornedTextField fullWidth key="peakshavingminsoc"
                                            label={"Peak shaving min soc"}
                                            info={`% of the battery capacity that is reserved for peak shaving (allowed range: ${peakShavingSocRangeText}).`}
                                            type={"text"}
                                            value={peakShavingMinSocDisplayValue}
                                            disabled={!editingConfig || !hasPeakShavingEligibleBattery}
                                            error={peakShavingMinSocRequiredMissing || peakShavingMinSocInvalid}
                                            helperText={!hasPeakShavingEligibleBattery ? "" :
                                                (peakShavingMinSocRequiredMissing ?
                                                    "Required when peak shaving is enabled" :
                                                    (peakShavingMinSocInvalid ? `Value must be an integer between ${minSocLimit} and ${maxSocLimit}` : ""))}
                                            onChange={(e) => handleConfigChange("peak_shaving_min_soc", e.target.value, true)}/>
                </Grid>
                {showBatteryCapacityVisualization && <Grid md={12} xs={12} item px={2} py={1}>
                    <BatteryCapacityVisualization
                        minSocLimit={minSocLimit}
                        maxSocLimit={maxSocLimit}
                        peakShavingMinSoc={peakShavingMinSocForVisualization}
                        peakShavingEnabled={peakShavingEnabled}
                        currentSoc={currentSoc}
                    />
                </Grid>}
            </Grid> : <Typography>No data available</Typography>}
    </EditableCard>
}
